/**
 * Shared Cart Module
 *
 * Handles bidirectional cart synchronization between Shopify and external storefronts.
 * - Cart Import: Decodes base64 handoff payload from URL and adds items to Shopify cart
 * - Cart Export: Encodes current Shopify cart to base64 and redirects to external storefront
 *
 * @module shared-cart
 */

import { importCart } from './import/index.js';
import { initCartExport } from './export/index.js';
import { initCartListeners } from './events/quantity.js';
import {
  isBackNavigation,
  getBackup,
  isBackupValid,
  clearBackup,
  restoreCart,
} from './cart/backup.js';
import {
  tryRestoreWarehouseCart,
  attachCheckoutClickClearing,
} from './cart/warehouse-cart-preservation.js';

// Re-export commonly used functions for external use
export { base64UrlEncode, base64UrlDecode } from './utils/base64.js';
export { getCart, addToCart } from './cart/api.js';
export { importCart } from './import/index.js';
export { initCartExport, buildExportPayload } from './export/index.js';

// Expose getLiveDiscountCodes on window so non-bundled scripts (e.g.
// assets/cart.js) can call it without duplicating the filtering logic.
import { getLiveDiscountCodes } from './cart/api.js';
window.getLiveDiscountCodes = getLiveDiscountCodes;

/**
 * Main initialization function. Awaits importCart so a fresh handoff is
 * applied to the cart BEFORE the warehouse-cart restore checks emptiness —
 * otherwise a handoff would populate the cart milliseconds after the restore
 * had already double-added items.
 */
async function init(): Promise<void> {
  console.info('[Shared Cart] Initializing');

  await importCart();
  initCartExport();
  initCartListeners();
  await tryRestoreWarehouseCart();
  attachCheckoutClickClearing();

  console.info('[Shared Cart] Initialized');
}

// Run immediately when the document has already parsed so async / late-injected
// bundles still initialize; otherwise wait for DOMContentLoaded.
if (document.readyState !== 'loading') {
  void init();
} else {
  document.addEventListener('DOMContentLoaded', init);
}

// Combined `pageshow` handler — DOMContentLoaded does not fire on bfcache
// restoration so we need a back-nav-aware path.
//   Forward nav → clear stale backup, return.
//   Back nav    → if a valid backup exists, run backup `restoreCart`
//                 (backup wins; warehouse entry stays for the next clean
//                 visit so we do NOT call `tryRestoreWarehouseCart` here).
//                 Else try the warehouse restore.
window.addEventListener('pageshow', (event) => {
  if (!isBackNavigation(event)) {
    clearBackup();
    return;
  }
  const backup = getBackup();
  if (backup && isBackupValid(backup)) {
    restoreCart().catch((error) => {
      console.error('[Shared Cart] Unexpected error during cart restore:', error);
    });
  } else {
    tryRestoreWarehouseCart().catch((error) => {
      console.error('[Shared Cart] Unexpected error during warehouse cart restore:', error);
    });
  }
});
